import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { locationsApi, LocationType, CreateLocationDto } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { CustomerSiteField } from '@/components/locations/CustomerSiteField';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

interface LocationFormData {
  name: string;
  code: string;
  type: LocationType;
  customerCompanyId: string | null;
  street: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  lat: string;
  lng: string;
  isActive: boolean;
}

export default function LocationsEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [formData, setFormData] = useState<LocationFormData>({
    name: '',
    code: '',
    type: 'SITE',
    customerCompanyId: null,
    street: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    lat: '',
    lng: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    locationsApi
      .getById(id)
      .then((response) => {
        const location = response;
        setFormData({
          name: location.name,
          code: location.code,
          type: location.type,
          customerCompanyId: location.customerCompanyId ?? null,
          street: location.address?.street || '',
          city: location.address?.city || '',
          state: location.address?.state || '',
          country: location.address?.country || '',
          postalCode: location.address?.postalCode || '',
          contactName: location.contact?.name || '',
          contactEmail: location.contact?.email || '',
          contactPhone: location.contact?.phone || '',
          lat: location.coordinates?.lat?.toString() || '',
          lng: location.coordinates?.lng?.toString() || '',
          isActive: location.isActive,
        });
      })
      .catch((error) => {
        toast.error(getErrorMessage(error));
        navigate('/locations');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.code || !formData.type) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      const payload: CreateLocationDto = {
        name: formData.name,
        code: formData.code,
        type: formData.type,
        customerCompanyId: formData.customerCompanyId,
        companyId: user?.companyId || '',
        isActive: formData.isActive,
      };

      if (
        formData.street ||
        formData.city ||
        formData.state ||
        formData.country ||
        formData.postalCode
      ) {
        payload.address = {
          street: formData.street || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          country: formData.country || undefined,
          postalCode: formData.postalCode || undefined,
        };
      }

      if (formData.contactName || formData.contactEmail || formData.contactPhone) {
        payload.contact = {
          name: formData.contactName || undefined,
          email: formData.contactEmail || undefined,
          phone: formData.contactPhone || undefined,
        };
      }

      if (formData.lat && formData.lng) {
        payload.coordinates = {
          lat: parseFloat(formData.lat),
          lng: parseFloat(formData.lng),
        };
      }

      await locationsApi.update(id!, payload);
      toast.success('Location updated successfully');
      navigate(`/locations/${id}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="Edit Location"
        description="Amendments to the recorded details of an existing site or depot."
        actions={
          <Button variant="outline" onClick={() => navigate(`/locations/${id}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Location
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Basic Information</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Houston Hub"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="HOU-001"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value: LocationType) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HUB">Hub</SelectItem>
                  <SelectItem value="SITE">Site</SelectItem>
                  <SelectItem value="DEPOT">Depot</SelectItem>
                  <SelectItem value="SUPPLIER">Supplier</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <CustomerSiteField
              value={formData.customerCompanyId}
              onChange={(customerCompanyId) => setFormData({ ...formData, customerCompanyId })}
            />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active Status</Label>
                <p className="text-sm text-muted-foreground">
                  {formData.isActive ? 'Location is active' : 'Location is inactive'}
                </p>
              </div>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>
          </div>

          {/* Address */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Address</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="street">Street</Label>
              <Input
                id="street"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                placeholder="123 Main St"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Houston"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State/Province</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="TX"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="USA"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder="77001"
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Contact</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="contactName">Contact Name</Label>
              <Input
                id="contactName"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactEmail">Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactPhone">Phone</Label>
                <Input
                  id="contactPhone"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  placeholder="+1 555-123-4567"
                />
              </div>
            </div>
          </div>

          {/* Coordinates */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Coordinates (Optional)</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lat">Latitude</Label>
                <Input
                  id="lat"
                  type="number"
                  step="any"
                  value={formData.lat}
                  onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                  placeholder="29.7604"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lng">Longitude</Label>
                <Input
                  id="lng"
                  type="number"
                  step="any"
                  value={formData.lng}
                  onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                  placeholder="-95.3698"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate(`/locations/${id}`)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Update Location'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
